import React, { useCallback, useEffect, useMemo, useState } from "react";
import Icons from "../icons";
import EpicSelect from "./EpicSelect";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

/**
 * Pick test cases by requirement instead of by board.
 *
 * Three levels, which is how a QA actually thinks about a cycle:
 *
 *     Ticket (SCRUM-67)
 *       └── Xray Test (XT-1)  — the manual test design
 *             └── Playwright test case — what Cortex will actually run
 *
 * You choose a project and an epic; every ticket in it that has Xray Tests is
 * listed. Tickets with no Xray Tests are shown separately and can't be picked —
 * an execution built from them couldn't report anything back to Jira.
 *
 * When `scopeTicketKeys` is given (the wizard was launched from a ticket) the
 * project/epic selectors are replaced by a fixed banner and only those tickets
 * are shown, so the same tree serves both entry points.
 *
 * Expanding an Xray Test shows its manual steps next to the Playwright steps
 * authored against each one, so you can see what a test does before selecting it.
 *
 * Selection is at the Playwright-case level; ticking a ticket or an Xray Test is
 * shorthand for everything underneath. `onToggleMany` receives the same case
 * shape the board picker emits, so the wizard's review and submit are unchanged.
 */
export default function TicketXrayPicker({
  isSelected,
  onToggleMany,
  scopeTicketKeys = null,
}) {
  const scoped = Array.isArray(scopeTicketKeys) && scopeTicketKeys.length > 0;
  const scopeParam = scoped ? scopeTicketKeys.join(",") : "";

  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState("");
  const [epics, setEpics] = useState([]);
  const [epic, setEpic] = useState("");
  const [epicsLoading, setEpicsLoading] = useState(false);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  const [openTickets, setOpenTickets] = useState(() => new Set());
  const [openTests, setOpenTests] = useState(() => new Set());

  // Projects — only needed when the user is choosing the scope themselves.
  useEffect(() => {
    if (scoped) return;
    fetch(`${API_BASE_URL}jira/projects`)
      .then((r) => r.json())
      .then((b) => {
        const list = Array.isArray(b?.data) ? b.data : [];
        setProjects(list);
        setProject((p) => p || (list[0] ? list[0].key : ""));
      })
      .catch(() => setProjects([]));
  }, [scoped]);

  // Epics for the chosen project
  useEffect(() => {
    if (scoped || !project) {
      setEpics([]);
      return;
    }
    setEpicsLoading(true);
    setEpic("");
    setData(null);
    fetch(`${API_BASE_URL}jira/epics?project=${encodeURIComponent(project)}`)
      .then((r) => r.json())
      .then((b) => setEpics(Array.isArray(b?.data) ? b.data : []))
      .catch(() => setEpics([]))
      .finally(() => setEpicsLoading(false));
  }, [project, scoped]);

  const load = useCallback(
    async (refresh = false) => {
      if (!scoped && !project) return;
      setLoading(true);
      setError("");
      try {
        const qs = scoped
          ? `tickets=${encodeURIComponent(scopeParam)}`
          : `project=${encodeURIComponent(project)}&epic=${encodeURIComponent(epic)}`;
        const res = await fetch(
          `${API_BASE_URL}coverage/epic?${qs}${refresh ? "&refresh=true" : ""}`,
        );
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          setData(null);
          setError(body?.detail || `HTTP ${res.status}`);
          return;
        }
        const d = body?.data || null;
        setData(d);
        // Open every scoped ticket (there are few); otherwise just the first, so
        // the shape of the tree is obvious without flooding the page.
        const keys = (d?.tickets || []).map((t) => t.ticket_key);
        setOpenTickets(new Set(scoped ? keys : keys.slice(0, 1)));
        setOpenTests(new Set());
      } catch (e) {
        setData(null);
        setError(`Could not load: ${e.message}`);
      } finally {
        setLoading(false);
      }
    },
    [project, epic, scoped, scopeParam],
  );

  // Loading an epic is one Xray call per ticket, so it waits for an explicit
  // epic choice rather than firing on every project change. A scoped picker has
  // its tickets up front, so it loads immediately.
  useEffect(() => {
    if (scoped || (project && epic)) load(false);
  }, [project, epic, scoped, load]);

  const toggleSet = (setter) => (key) =>
    setter((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  const toggleTicket = toggleSet(setOpenTickets);
  const toggleTest = toggleSet(setOpenTests);

  const tickets = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data?.tickets || [];
    return (data?.tickets || []).filter((t) => {
      if (`${t.ticket_key} ${t.summary}`.toLowerCase().includes(q)) return true;
      return (t.tests || []).some(
        (x) =>
          `${x.key} ${x.summary}`.toLowerCase().includes(q) ||
          (x.testcases || []).some((c) => c.name.toLowerCase().includes(q)),
      );
    });
  }, [data, query]);

  // Every Playwright case under an Xray test / a ticket, in the shape the
  // wizard stores. `ticket_key` rides along so the wizard can link the execution
  // to the right Jira story without asking the user a second time.
  const casesOf = (xrayTest, ticket) =>
    (xrayTest.testcases || []).map((c) => ({
      testcasekey: c.testcasekey,
      name: c.name,
      board_id: c.board_id,
      board_name: c.board_name,
      priority: c.priority,
      ticket_key: ticket?.ticket_key || "",
      xray_test_key: xrayTest.key,
    }));
  const casesOfTicket = (ticket) =>
    (ticket.tests || []).flatMap((x) => casesOf(x, ticket));

  const allSelected = (cases) =>
    cases.length > 0 && cases.every((c) => isSelected(c.testcasekey));
  const someSelected = (cases) => cases.some((c) => isSelected(c.testcasekey));

  const CheckBox = ({ checked, indeterminate, onChange, disabled, title }) => (
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      title={title}
      ref={(el) => {
        if (el) el.indeterminate = !checked && !!indeterminate;
      }}
      onChange={onChange}
      onClick={(e) => e.stopPropagation()}
      className="h-3.5 w-3.5 flex-none rounded border-slate-300 text-brand-600 disabled:cursor-not-allowed disabled:opacity-40"
    />
  );

  return (
    <div className="space-y-3">
      {/* ---- Scope: fixed to the launching ticket, or project + epic ---- */}
      {scoped ? (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-brand-200 bg-brand-50/70 px-3 py-2">
          <Icons.Check className="h-3.5 w-3.5 flex-none text-brand-600" />
          <span className="text-xs text-brand-900">
            Scoped to{" "}
            <span className="font-mono font-semibold">{scopeTicketKeys.join(", ")}</span>{" "}
            — its automated tests are pre-selected. Untick anything you don't want
            in this cycle.
          </span>
          <button
            type="button"
            onClick={() => load(true)}
            disabled={loading}
            className="ml-auto inline-flex items-center gap-1 rounded-md border border-brand-300 bg-white px-2 py-1 text-[0.6875rem] font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50"
          >
            <Icons.Refresh className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
            Reload
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)_auto]">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-600">Project</span>
            <select
              value={project}
              onChange={(e) => setProject(e.target.value)}
              className="h-[2.375rem] w-full rounded-lg border border-slate-300 bg-white px-3 text-sm"
            >
              {projects.length === 0 && <option value="">Loading…</option>}
              {projects.map((p) => (
                <option key={p.id || p.key} value={p.key}>
                  {p.name} ({p.key})
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-600">Epic</span>
            <EpicSelect
              value={epic}
              onChange={setEpic}
              epics={epics}
              loading={epicsLoading}
              className="w-full"
            />
          </label>
          <div className="flex items-end">
            <button
              type="button"
              onClick={() => load(true)}
              disabled={!project || loading}
              className="inline-flex h-[2.375rem] items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              <Icons.Refresh className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Reload
            </button>
          </div>
        </div>
      )}

      {!scoped && !epic && !loading && !data && (
        <p className="rounded-xl border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">
          Pick an epic to list its tickets, their tests, and the Artifacts
          you can run.
        </p>
      )}

      {loading && (
        <p className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
          <Icons.Spinner className="h-4 w-4 animate-spin" />
          Reading each ticket's tests and their Artifacts…
        </p>
      )}

      {error && (
        <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </p>
      )}

      {data && !loading && (
        <>
          {/* Roll-up + search */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-slate-500">
              <span className="font-semibold text-slate-800">
                {data.totals?.tickets || 0}
              </span>{" "}
              ticket(s) with tests ·{" "}
              <span className="font-semibold text-slate-800">
                {data.totals?.automated || 0}
              </span>
              {" / "}
              {data.totals?.xray_tests || 0} tests automated ·{" "}
              <span className="font-semibold text-slate-800">
                {data.totals?.testcases || 0}
              </span>{" "}
              runnable test case(s)
            </p>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by ticket, test key or test name…"
              className="w-56 rounded-lg border border-slate-300 px-3 py-1.5 text-xs"
            />
          </div>

          {data.truncated && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[0.6875rem] text-amber-800">
              Showing the first {data.tickets.length} tickets of this epic. Narrow it
              down with a more specific epic if something's missing.
            </p>
          )}

          {/* ---- The tree ---- */}
          {tickets.length === 0 ? (
            <p className="rounded-xl border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">
              {query
                ? "Nothing matches that filter."
                : "No ticket in this epic has tests yet."}
            </p>
          ) : (
            <ul className="space-y-2">
              {tickets.map((t) => {
                const tCases = casesOfTicket(t);
                const open = openTickets.has(t.ticket_key);
                return (
                  <li
                    key={t.ticket_key}
                    className="overflow-hidden rounded-xl border border-slate-200 bg-white"
                  >
                    {/* ---- Level 1: ticket ---- */}
                    <div className="flex items-center gap-2 bg-slate-50/80 px-3 py-2">
                      <CheckBox
                        checked={allSelected(tCases)}
                        indeterminate={someSelected(tCases)}
                        disabled={tCases.length === 0}
                        title={
                          tCases.length === 0
                            ? "No test on this ticket has an Artifact yet."
                            : "Select every Artifact under this ticket"
                        }
                        onChange={() => onToggleMany(tCases)}
                      />
                      <button
                        type="button"
                        onClick={() => toggleTicket(t.ticket_key)}
                        className="flex min-w-0 flex-1 items-center gap-2 text-left"
                      >
                        <Icons.ChevronRight
                          className={`h-3.5 w-3.5 flex-none text-slate-400 transition-transform ${
                            open ? "rotate-90" : ""
                          }`}
                        />
                        <span className="font-mono text-xs font-semibold text-brand-700">
                          {t.ticket_key}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-xs text-slate-700">
                          {t.summary}
                        </span>
                        {t.status && (
                          <span className="flex-none rounded-full bg-white px-2 py-0.5 text-[0.625rem] text-slate-600 ring-1 ring-slate-200">
                            {t.status}
                          </span>
                        )}
                        <span className="flex-none rounded-full bg-violet-100 px-2 py-0.5 text-[0.625rem] font-semibold text-violet-700">
                          {t.total_xray} Test{t.total_xray === 1 ? "" : "s"}
                        </span>
                        <span
                          className={`flex-none rounded-full px-2 py-0.5 text-[0.625rem] font-semibold ${
                            t.testcase_count
                              ? "bg-emerald-100 text-emerald-800"
                              : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          {t.testcase_count} runnable
                        </span>
                      </button>
                    </div>

                    {open && (
                      <ul className="divide-y divide-slate-100">
                        {(t.tests || []).map((x) => {
                          const xCases = casesOf(x, t);
                          const xOpen = openTests.has(`${t.ticket_key}:${x.key}`);
                          return (
                            <li key={x.key}>
                              {/* ---- Level 2: Xray test ---- */}
                              <div className="flex items-center gap-2 px-3 py-1.5 pl-8">
                                <CheckBox
                                  checked={allSelected(xCases)}
                                  indeterminate={someSelected(xCases)}
                                  disabled={xCases.length === 0}
                                  title={
                                    xCases.length === 0
                                      ? "No Artifact authored from this test yet."
                                      : "Select every Artifact for this test"
                                  }
                                  onChange={() => onToggleMany(xCases)}
                                />
                                <button
                                  type="button"
                                  onClick={() => toggleTest(`${t.ticket_key}:${x.key}`)}
                                  className="flex min-w-0 flex-1 items-center gap-2 text-left"
                                >
                                  <Icons.ChevronRight
                                    className={`h-3 w-3 flex-none text-slate-300 transition-transform ${
                                      xOpen ? "rotate-90" : ""
                                    }`}
                                  />
                                  <span className="font-mono text-[0.6875rem] font-semibold text-violet-700">
                                    {x.key}
                                  </span>
                                  <span className="min-w-0 flex-1 truncate text-[0.6875rem] text-slate-700">
                                    {x.summary || "(no summary)"}
                                  </span>
                                  <span className="flex-none rounded-full bg-slate-100 px-1.5 py-0.5 text-[0.625rem] text-slate-600">
                                    {x.steps_count} step
                                    {x.steps_count === 1 ? "" : "s"}
                                  </span>
                                  {x.automated ? (
                                    <span className="flex-none rounded-full bg-emerald-100 px-1.5 py-0.5 text-[0.625rem] font-semibold text-emerald-800">
                                      {x.testcases.length} playwright test
                                      {x.testcases.length === 1 ? "" : "s"}
                                    </span>
                                  ) : (
                                    <span className="flex-none rounded-full bg-amber-100 px-1.5 py-0.5 text-[0.625rem] font-semibold text-amber-800">
                                      not automated
                                    </span>
                                  )}
                                </button>
                              </div>

                              {/* ---- Level 3: Playwright cases ---- */}
                              {x.automated && (
                                <ul className="pl-14 pr-3 pb-1.5">
                                  {(x.testcases || []).map((c) => (
                                    <li
                                      key={c.testcasekey}
                                      className="flex items-center gap-2 py-1"
                                    >
                                      <CheckBox
                                        checked={isSelected(c.testcasekey)}
                                        onChange={() =>
                                          onToggleMany([
                                            {
                                              testcasekey: c.testcasekey,
                                              name: c.name,
                                              board_id: c.board_id,
                                              board_name: c.board_name,
                                              priority: c.priority,
                                              ticket_key: t.ticket_key,
                                              xray_test_key: x.key,
                                            },
                                          ])
                                        }
                                      />
                                      <Icons.Play className="h-3 w-3 flex-none text-brand-500" />
                                      <span className="min-w-0 flex-1 truncate text-[0.6875rem] font-medium text-slate-800">
                                        {c.name}
                                      </span>
                                      <span className="flex-none rounded-full bg-slate-50 px-1.5 py-0.5 text-[0.625rem] text-slate-500 ring-1 ring-slate-200">
                                        {c.board_name}
                                      </span>
                                      <span className="flex-none text-[0.625rem] text-slate-400">
                                        {c.steps_count} step
                                        {c.steps_count === 1 ? "" : "s"}
                                      </span>
                                      {c.drifted && (
                                        <span className="flex-none rounded-full bg-amber-100 px-1.5 py-0.5 text-[0.625rem] font-semibold text-amber-800">
                                          Test steps changed
                                        </span>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              )}

                              {/* Xray step -> Playwright step mapping */}
                              {xOpen && (
                                <div className="mx-3 mb-2 ml-14 overflow-hidden rounded-lg border border-slate-200">
                                  <table className="w-full table-fixed text-[0.6875rem]">
                                    <caption className="sr-only">
                                      {x.key} manual steps and the Artifact steps
                                      authored against each
                                    </caption>
                                    <thead className="bg-slate-50 text-left text-slate-500">
                                      <tr>
                                        <th scope="col" className="w-8 px-2 py-1 font-semibold">
                                          #
                                        </th>
                                        <th scope="col" className="w-2/5 px-2 py-1 font-semibold">
                                          Test step
                                        </th>
                                        <th scope="col" className="px-2 py-1 font-semibold">
                                          Artifact steps
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(x.xray_steps || []).length === 0 ? (
                                        <tr>
                                          <td
                                            colSpan={3}
                                            className="px-2 py-2 text-slate-400"
                                          >
                                            This test has no manual steps.
                                          </td>
                                        </tr>
                                      ) : (
                                        (x.xray_steps || []).map((xs) => {
                                          const pw = (x.testcases || []).flatMap((c) =>
                                            (c.steps || []).filter(
                                              (s) => s.source_step_no === xs.step_no,
                                            ),
                                          );
                                          return (
                                            <tr
                                              key={xs.step_no}
                                              className="border-t border-slate-100 align-top"
                                            >
                                              <td className="px-2 py-1 text-slate-400">
                                                {xs.step_no}
                                              </td>
                                              <td className="px-2 py-1 text-slate-700">
                                                <div>
                                                  {/* Cucumber scenario line: keep its keyword. */}
                                                  {xs.keyword && <span className="font-mono font-semibold text-blue-700">{xs.keyword} </span>}
                                                  {xs.action || "—"}
                                                </div>
                                                {xs.result && (
                                                  <div className="mt-0.5 text-slate-500">
                                                    Expected: {xs.result}
                                                  </div>
                                                )}
                                              </td>
                                              <td className="px-2 py-1">
                                                {pw.length === 0 ? (
                                                  <span className="italic text-amber-600">
                                                    No Artifact step found.
                                                  </span>
                                                ) : (
                                                  <ul className="space-y-0.5">
                                                    {pw.map((s, i) => (
                                                      <li key={i} className="flex gap-1.5">
                                                        <span className="rounded bg-brand-50 px-1 font-mono font-semibold text-brand-700">
                                                          {s.action}
                                                        </span>
                                                        <span className="break-all text-slate-600">
                                                          {s.selector}
                                                          {s.value ? ` = "${s.value}"` : ""}
                                                        </span>
                                                      </li>
                                                    ))}
                                                  </ul>
                                                )}
                                              </td>
                                            </tr>
                                          );
                                        })
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {/* Tickets that can't take part */}
          {(data.skipped_no_xray || []).length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
              <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-500">
                Not listed — no tests ({data.skipped_no_xray.length})
              </p>
              <p className="mt-1 text-[0.6875rem] leading-snug text-slate-500">
                {data.skipped_no_xray.map((t) => t.ticket_key).join(", ")}
              </p>
              <p className="mt-1 text-[0.625rem] leading-snug text-slate-400">
                A test with no test steps behind it has no step to report results
                against, so it can't be part of an execution that can be reported to Jira.
              </p>
            </div>
          )}

          {(data.errors || []).length > 0 && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[0.6875rem] text-amber-800">
              {data.errors.join(" · ")}
            </p>
          )}
        </>
      )}
    </div>
  );
}
